import { GLSL_CONSTS, GLSL_HASH_NOISE, GLSL_SDF } from './common';
import { GLSL_RIPPLE_READ } from './ripple';

/**
 * Main mandala pass. One full-screen fragment shader; every element is an SDF anti-aliased with an
 * fwidth-derived width so lines are crisp at any DPR.
 *
 * Coordinates: css pixels, origin top-left, y down (same as geometry.ts). Angles counter-clockwise
 * from east. Radii come straight from geometry.layoutFor via uniforms so visuals and hit-testing agree.
 *
 * Defines: MRT (second output = emissive for bloom, tier 2), RIPPLE_PACKED (RGBA8 ripple storage),
 * WAVE_UNORM (R8 waveform texture instead of R16F).
 *
 * Zone uniforms: uZoneA[i] = (ring, index, slot, amplitude); uZoneB[i] = (age s, latched, releasing, family idx).
 * Family index: 0 white, 1 blue, 2 yellow, 3 red, 4 green (palette.FAMILY_INDEX).
 */
export const MANDALA_FRAG = /* glsl */ `#version 300 es
precision highp float;
precision highp int;
precision highp sampler2D;

in vec2 vUv;
layout(location = 0) out vec4 outColor;
#ifdef MRT
layout(location = 1) out vec4 outEmissive;
#endif

uniform vec2 uCanvasPx;      // drawing-buffer size in device px
uniform vec2 uCanvasCss;     // css px
uniform float uDpr;
uniform vec2 uCentre;        // css px
uniform float uR;            // outer radius of R3 (css px)
uniform float uRadii[4];     // outer radii of R0..R3 (css px)
uniform float uPetals;       // degrees + 1
uniform float uTime;         // seconds (frozen in calm mode)
uniform float uWall;         // wall-clock seconds (never frozen; calm-mode rings)
uniform float uCalm;
uniform float uTheme;        // 0 dark, 1 light
uniform float uReveal;       // 0 before first touch → 1
uniform float uPetalOpen[16];
uniform float uRingRot[5];   // ornament rotation per ring (rad, CCW)
uniform float uMelt[5];      // 0..0.15 radial domain warp per ring
uniform vec3 uRingCol[4];    // family colour per ring R0..R3
uniform float uScaleMask[12];
uniform float uScaleMaskPrev[12];
uniform float uTonicSector;  // fire sector index of the tonic
uniform float uTonicWave;    // 0..1 progress of the tonic-change wave, < 0 idle
uniform float uScaleMorph;   // 0..1 progress of the Escher metamorphosis, < 0 idle
uniform vec3 uTint;          // master-centroid highlight tint
uniform float uSunburst;     // 0..0.08
uniform vec4 uZoneA[16];
uniform vec4 uZoneB[16];
uniform int uZoneCount;
uniform vec2 uLabels[48];    // normalised 0..1 canvas coords
uniform int uLabelCount;
uniform float uLabelRadius;  // css px
uniform vec4 uCalmRings[8];  // x, y, t0, strength (calm-mode analytic ripples)
uniform int uCalmRingCount;
uniform sampler2D uRipple;
uniform vec2 uSimCentre;
uniform float uSimSpan;
uniform float uSimTexel;
uniform float uRefract;      // css px of displacement per unit height gradient
uniform sampler2D uNebula;
uniform sampler2D uWave;     // 256×16 waveform rows

${GLSL_CONSTS}
${GLSL_HASH_NOISE}
${GLSL_SDF}
${GLSL_RIPPLE_READ}

float waveSample(float t, float slot) {
  float v = textureLod(uWave, vec2(t, (slot + 0.5) / 16.0), 0.0).r;
#ifdef WAVE_UNORM
  v = v * 2.0 - 1.0;
#endif
  return v;
}

// glow variants of the five Buddha-family hues (white, blue, yellow, red, green)
const vec3 FAMG[5] = vec3[5](
  vec3(1.000, 0.965, 0.878),
  vec3(0.310, 0.482, 0.910),
  vec3(1.000, 0.824, 0.353),
  vec3(0.941, 0.333, 0.247),
  vec3(0.310, 0.820, 0.627));

float bandMask(float r, float lo, float hi, float s) {
  return smoothstep(lo - s, lo + s, r) * (1.0 - smoothstep(hi - s, hi + s, r));
}

void main() {
  // ---------------------------------------------------------------- coordinates
  vec2 p = vec2(gl_FragCoord.x, uCanvasPx.y - gl_FragCoord.y) / uDpr;
  float dark = 1.0 - uTheme;
  float r0 = uRadii[0];
  float r1 = uRadii[1];
  float r2 = uRadii[2];
  float r3 = uRadii[3];

  // ---------------------------------------------------------------- ripple height + gradient
  float hC = 0.0;
  vec2 grad = vec2(0.0);
  if (uCalm < 0.5) {
    vec2 suv = (p - uSimCentre) / uSimSpan + 0.5;
    float hl = rippleH(uRipple, suv - vec2(uSimTexel, 0.0));
    float hr = rippleH(uRipple, suv + vec2(uSimTexel, 0.0));
    float hd = rippleH(uRipple, suv - vec2(0.0, uSimTexel));
    float hu = rippleH(uRipple, suv + vec2(0.0, uSimTexel));
    hC = rippleH(uRipple, suv);
    grad = vec2(hr - hl, hu - hd);
  }
  // refraction: displace the whole pattern by the height-field gradient
  vec2 pw = p + grad * uRefract;
  // anti-aliasing width, computed once in uniform control flow
  float aa = max(fwidth(pw.x), fwidth(pw.y)) * 0.7 + 1e-4;

  vec2 d = pw - uCentre;
  vec2 m = vec2(d.x, -d.y);                     // y-up frame for Cartesian ornament
  float rr = length(d);
  float a = atan(m.y, m.x);
  if (a < 0.0) a += TAU;
  vec2 dir = rr > 0.0 ? m / rr : vec2(1.0, 0.0);

  // ---------------------------------------------------------------- melt: continuous radial domain warp
  float bwid = 0.25 * uR;
  float meltField = uMelt[0] * (1.0 - smoothstep(r0 - 8.0, r0 + 8.0, rr))
                  + uMelt[1] * bandMask(rr, r0, r1, 8.0)
                  + uMelt[2] * bandMask(rr, r1, r2, 8.0)
                  + uMelt[3] * bandMask(rr, r2, r3, 8.0)
                  + uMelt[4] * smoothstep(r3 - 8.0, r3 + 8.0, rr);
  float r = rr;
  if (meltField > 0.0005) {
    float mn = fbm3(dir * 2.2 + vec2(uTime * 0.05, -uTime * 0.03)) * 2.0 - 1.0;
    r += meltField * bwid * mn;
  }

  // ---------------------------------------------------------------- zone classification (mirrors geometry.ts)
  int ring = r < r0 ? 0 : (r < r1 ? 1 : (r < r2 ? 2 : (r < r3 ? 3 : 4)));
  int rc = min(ring, 3);
  float w = TAU / uPetals;
  float lp = foldSigned(a, uPetals);            // -w/2..w/2, 0 at the petal centre
  int petal = min(int(floor(mod(a + w * 0.5, TAU) / w)), int(uPetals) - 1);
  float tz = lp / w + 0.5;                      // 0..1 across the petal
  float wf = TAU / 12.0;
  float lf = foldSigned(a, 12.0);
  int sector = min(int(floor(mod(a + wf * 0.5, TAU) / wf)), 11);
  float tzf = lf / wf + 0.5;
  int idx = ring == 4 ? sector : petal;
  float inner = ring == 0 ? 0.0 : (ring == 1 ? r0 : (ring == 2 ? r1 : (ring == 3 ? r2 : r3)));
  float outer = ring == 0 ? r0 : (ring == 1 ? r1 : (ring == 2 ? r2 : (ring == 3 ? r3 : r3 + 0.5 * uR)));
  float bandT = clamp((r - inner) / max(outer - inner, 1.0), 0.0, 1.0);

  // ---------------------------------------------------------------- palette
  const vec3 GOLD = vec3(0.831, 0.686, 0.216);
  const vec3 GOLD_HI = vec3(1.000, 0.878, 0.541);
  const vec3 GOLD_SH = vec3(0.549, 0.427, 0.122);
  const vec3 VERM = vec3(0.890, 0.259, 0.204);
  const vec3 VERM_DK = vec3(0.702, 0.149, 0.118);
  const vec3 AZURITE = vec3(0.122, 0.227, 0.576);
  const vec3 MALACHITE = vec3(0.114, 0.541, 0.420);
  const vec3 CINNABAR = vec3(0.702, 0.149, 0.118);
  const vec3 ORPIMENT = vec3(0.910, 0.722, 0.188);
  vec3 ground = mix(vec3(0.043, 0.039, 0.059), vec3(0.953, 0.933, 0.890), uTheme);
  float shim = 0.82 + 0.18 * sin(a * 36.0 + r * 0.06 - uTime * 1.6) * (0.4 + 0.6 * vnoise(pw * 0.04 + uTime * 0.15));
  vec3 lineCol = mix(GOLD, VERM, uTheme) * shim;
  vec3 lineHi = mix(mix(GOLD_HI, uTint, 0.3), mix(vec3(1.0, 0.45, 0.35), uTint, 0.2), uTheme);
  vec3 lineDk = mix(GOLD_SH * 0.35, VERM_DK, uTheme);
  float rv = mix(0.42, 1.0, uReveal);

  // ---------------------------------------------------------------- background: nebula through the ground
  vec3 neb = textureLod(uNebula, vUv, 0.0).rgb;
  float nebMask = 0.36 + 0.64 * (1.0 - smoothstep(r0 - 6.0, r0 + 3.0, rr)) + 0.64 * smoothstep(r3 - 3.0, r3 + 6.0, rr);
  float nl = dot(neb, vec3(0.299, 0.587, 0.114));
  vec3 colDark = ground + neb * nebMask;
  vec3 colLight = ground * (1.0 - 0.3 * nl * nebMask) + neb * 0.12 * nebMask;
  vec3 col = mix(colDark, colLight, uTheme);

  // ring fills: family-tinted translucent bands, brighter toward the outer edge
  if (ring < 4) {
    col = mix(col, uRingCol[rc], (0.07 + 0.05 * bandT) * rv);
  }

  vec3 emis = vec3(0.0);
  float goldCov = 0.0;
  float darkCov = 0.0;
  float glow = 0.0;

  // ---------------------------------------------------------------- fire ring (R4): 12 sectors to the page edge
  float sel = 1.0;
  float waveGlow = 0.0;
  if (uTonicWave >= 0.0) {
    float aRel = mod(a - uTonicSector * wf + TAU, TAU);
    float dd = uTonicWave * TAU - aRel;         // > 0: the front has passed this angle
    sel = smoothstep(-0.12, 0.12, dd);
    waveGlow = (dd >= 0.0 ? exp(-dd * 4.0) : exp(dd * 30.0)) * (1.0 - uTonicWave);
  }
  float inScale = mix(uScaleMaskPrev[sector], uScaleMask[sector], sel);
  float isTonic = abs(float(sector) - uTonicSector) < 0.5 ? 1.0 : 0.0;
  if (ring == 4) {
    float fs = float(sector);
    float fl = fbm3(vec2(lf * 9.0 + fs * 7.3, (rr - r3) * 0.012 - uTime * 0.35));
    float fl2 = fbm3(vec2(lf * 5.0 - fs * 3.1, (rr - r3) * 0.006 - uTime * 0.2) + 11.0);
    float flame = smoothstep(0.25, 0.85, fl * 0.65 + fl2 * 0.5);
    float fall = exp(-(rr - r3) / (0.42 * uR));
    vec3 emberIn = mix(CINNABAR, vec3(0.949, 0.549, 0.157), flame);
    vec3 emberOut = mix(vec3(0.10, 0.05, 0.06), vec3(0.24, 0.12, 0.10), flame);
    vec3 emberTon = mix(GOLD, GOLD_HI, flame) + vec3(0.3, 0.25, 0.15) * exp(-(rr - r3) / (0.08 * uR));
    vec3 ember = mix(mix(emberOut, emberIn, inScale), emberTon, isTonic);
    vec3 emberL = mix(vec3(0.85, 0.78, 0.70), mix(VERM, vec3(0.949, 0.549, 0.157), flame), inScale);
    emberL = mix(emberL, mix(GOLD, vec3(0.9, 0.6, 0.1), flame), isTonic);
    vec3 emberCol = mix(ember, emberL, uTheme);
    float eA = mix(mix(0.14, 0.62, inScale), 0.95, isTonic) * (0.55 + 0.45 * flame) * fall;
    col = mix(col, emberCol, eA * mix(1.0, 0.75, uTheme));
    col += lineHi * waveGlow * 0.6 * fall;
    emis += emberCol * eA * (0.25 + 0.5 * isTonic) + lineHi * waveGlow * 0.5 * fall;
    float dDiv = rr * sin(fold(a + wf * 0.5, 12.0));
    col = mix(col, lineCol, stroke(dDiv, 0.6, aa) * 0.3 * rv);
    glow += glowOf(dDiv - 0.6, 0.15) * 0.08;
  }

  // ---------------------------------------------------------------- ring boundaries, thangka double outline
  for (int i = 0; i < 4; i++) {
    float dR = r - uRadii[i];
    float wdt = (i == 2) ? 1.5 : 1.0;
    goldCov = max(goldCov, stroke(dR, wdt, aa));
    darkCov = max(darkCov, stroke(dR + wdt + 2.0, 0.6, aa));
    glow += glowOf(abs(dR) - wdt, 0.18) * 0.35;
  }

  // petal dividers through R1..R3 (petals = degrees + 1, petal 0 centred on east)
  float inRings = bandMask(r, r0, r3, 1.5);
  float dPd = r * sin(fold(a + w * 0.5, uPetals));
  goldCov = max(goldCov, stroke(dPd, 0.8, aa) * inRings);
  darkCov = max(darkCov, stroke(dPd - 2.4, 0.5, aa) * inRings);
  glow += glowOf(dPd - 0.8, 0.2) * 0.25 * inRings;

  // flower of life + dharmachakra 8 spokes, low intensity, R0..R2
  float Rf = r1 * 0.5;
  float la6 = fold(a, 6.0);
  vec2 q6 = r * vec2(cos(la6), sin(la6));
  float dFol = min(abs(r - Rf), abs(length(q6 - vec2(Rf, 0.0)) - Rf));
  float dSp = r * sin(fold(a, 8.0));
  float lowMask = bandMask(r, r0, r2, 2.0) * rv;
  col = mix(col, lineCol, (stroke(dFol, 0.6, aa) * 0.12 + stroke(dSp, 0.6, aa) * 0.14) * lowMask);

  // ---------------------------------------------------------------- R0 bindu
  if (ring == 0) {
    float e = smoothstep(r0 * 0.45, r0, r);
    col = mix(col + FAMG[0] * 0.10 * e * rv * dark, mix(col, vec3(1.0), 0.15 * e), uTheme);
    float la = fold(a + uRingRot[0], 8.0);
    vec2 q0 = r * vec2(cos(la), sin(la)) - vec2(r0 * 0.5, 0.0);
    float dSeed = sdVesica(vec2(q0.y, q0.x), r0 * 0.34, r0 * 0.2);
    goldCov = max(goldCov, stroke(dSeed, 0.7, aa) * 0.7 * rv);
    float cdot = fillIn(r - max(2.5, r0 * 0.05), aa);
    col = mix(col, lineHi, cdot);
    emis += lineHi * cdot;
    glow += glowOf(r - 3.0, 0.15) * 0.6;
  }

  // ---------------------------------------------------------------- R1 lotus: vesica petals
  if (ring == 1) {
    float open = uPetalOpen[min(petal, 15)];
    float op = max(open, 0.02);
    float L = (r1 - r0) * 0.84 * op;
    float rBase = r0 + (r1 - r0) * 0.08;
    vec2 q = vec2(r - (rBase + L * 0.5), r * sin(lp));   // radial, tangential
    float b = L * 0.5;
    float hw = min(0.36 * (r0 + r1) * 0.5 * w * op, b * 0.95);   // petals stay longer than wide
    float rvv = (b * b + hw * hw) / (2.0 * hw);
    float dvv = rvv - hw;
    float dV = sdVesica(vec2(q.y, q.x), rvv, dvv);
    float dV2 = sdVesica(vec2(q.y, q.x) / 0.62, rvv, dvv) * 0.62;
    float show = smoothstep(0.0, 0.08, open);
    float pfill = fillIn(dV, aa) * show;
    float g = clamp((q.x + b) / max(2.0 * b, 1.0), 0.0, 1.0);
    col = mix(col, uRingCol[1], pfill * (0.12 + 0.22 * g) * rv);
    float pc = stroke(dV, 0.9, aa) * show;
    float pc2 = stroke(dV2, 0.6, aa) * show * 0.6;
    float vein = stroke(q.y, 0.5, aa) * step(abs(q.x), b * 0.85) * 0.45 * show;
    goldCov = max(goldCov, max(pc, max(pc2, vein)));
    darkCov = max(darkCov, stroke(dV + 2.2, 0.5, aa) * show);
    glow += glowOf(dV - 0.9, 0.25) * 0.3 * show;
    emis += uRingCol[1] * pfill * g * 0.12;
  }

  // ---------------------------------------------------------------- R2 palace: square in circle, four gates
  if (ring == 2) {
    float sq = r2 * 0.70;
    float dSq = abs(sdBox(m, vec2(sq)));
    float dSq2 = abs(sdBox(rot(m, PI * 0.25), vec2(sq * 0.74)));
    goldCov = max(goldCov, max(stroke(dSq, 1.0, aa), stroke(dSq2, 0.7, aa) * 0.7));
    darkCov = max(darkCov, stroke(dSq - 2.4, 0.5, aa));
    glow += glowOf(dSq - 1.0, 0.2) * 0.25;
    // rotating lattice ornament, kaleidoscopic fold at the petal count
    float la2 = fold(a + uRingRot[2], uPetals);
    float rm2 = (r1 + r2) * 0.5;
    float bw2 = r2 - r1;
    vec2 q2 = r * vec2(cos(la2), sin(la2)) - vec2(rm2, 0.0);
    float dRh = abs(sdRhombus(q2, vec2(bw2 * 0.30, bw2 * 0.15)));
    float dJ = abs(sdCircle(q2, bw2 * 0.06));
    goldCov = max(goldCov, max(stroke(dRh, 0.6, aa), stroke(dJ, 0.6, aa)) * 0.35);
    // gates: E blue, N green, W red, S yellow (Vajradhatu)
    for (int gI = 0; gI < 4; gI++) {
      vec2 lg = rot(m, -float(gI) * PI * 0.5);
      float gd = sq * 0.16;
      float gw = sq * 0.09;
      float dStem = sdBox(lg - vec2(sq + gd * 0.5, 0.0), vec2(gd * 0.5, gw));
      float dLintel = sdBox(lg - vec2(sq + gd * 1.1, 0.0), vec2(gd * 0.18, gw * 1.8));
      float dG = min(dStem, dLintel);
      float gf = fillIn(dG, aa);
      vec3 gc = gI == 0 ? AZURITE : (gI == 1 ? MALACHITE : (gI == 2 ? CINNABAR : ORPIMENT));
      col = mix(col, gc, gf * 0.85 * rv);
      goldCov = max(goldCov, stroke(dG, 0.8, aa));
      emis += gc * gf * 0.35;
    }
  }

  // ---------------------------------------------------------------- R3 vajra ring + Escher metamorphosis cells
  if (ring == 3) {
    float n3 = uPetals * 2.0;
    float la3 = fold(a + uRingRot[3], n3);
    float rm3 = (r2 + r3) * 0.5;
    float bw3 = r3 - r2;
    vec2 q3 = r * vec2(cos(la3), sin(la3)) - vec2(rm3, 0.0);
    float cs = bw3 * 0.40;
    float u = 0.0;
    if (uScaleMorph >= 0.0) {
      float front = uScaleMorph * (TAU + 1.4);
      u = clamp((front - a) / 1.4, 0.0, 1.0);
    }
    float mu = sin(u * PI) * 2.0;                 // 0 hexagon → 1 petal → 2 bird → back
    float wi = sin(u * PI);
    float dHex = sdHexagon(q3, cs * 0.8);
    float dPet = sdVesica(vec2(q3.y, q3.x), cs, cs * 0.55);
    float dBd = sdBird(q3, cs * 1.4);
    float dCell = mu < 1.0 ? mix(dHex, dPet, mu) : mix(dPet, dBd, mu - 1.0);
    goldCov = max(goldCov, stroke(dCell, 0.7, aa) * (0.22 + 0.6 * wi));
    col = mix(col, uRingCol[3], fillIn(dCell, aa) * 0.14 * wi);
    // vajra glyph: rhombus hub with two prong loops; fades under the wave
    float vj = 1.0 - wi;
    float dRh3 = abs(sdRhombus(q3, vec2(bw3 * 0.22, bw3 * 0.09)));
    float dP1 = abs(sdCircle(q3 - vec2(bw3 * 0.26, 0.0), bw3 * 0.11));
    float dP2 = abs(sdCircle(q3 + vec2(bw3 * 0.26, 0.0), bw3 * 0.11));
    goldCov = max(goldCov, max(stroke(dRh3, 0.7, aa), max(stroke(dP1, 0.6, aa), stroke(dP2, 0.6, aa))) * 0.6 * vj);
    // pearls on the cell boundaries
    float la3b = fold(a + uRingRot[3] + PI / n3, n3);
    vec2 q3b = r * vec2(cos(la3b), sin(la3b)) - vec2(rm3, 0.0);
    float pearl = fillIn(sdCircle(q3b, bw3 * 0.05), aa) * 0.8 * vj * rv;
    col = mix(col, lineHi, pearl);
    emis += lineHi * pearl * 0.3;
    glow += glowOf(dCell - 0.7, 0.3) * 0.15;
  }

  // ---------------------------------------------------------------- zone activity: glow + polar waveform rings
  vec3 zoneGlow = vec3(0.0);
  vec3 zoneTint = vec3(0.0);
  float zoneW = 0.0;
  vec3 waveCol = vec3(0.0);
  float waveA = 0.0;
  float rMid = (inner + outer) * 0.5;
  float band = outer - inner;
  if (ring == 0) { rMid = r0 * 0.68; band = r0 * 0.6; }
  if (ring == 4) { rMid = r3 + 0.13 * uR; band = 0.22 * uR; }
  float tt = ring == 0 ? a / TAU : (ring == 4 ? tzf : tz);
  for (int i = 0; i < 16; i++) {
    if (i >= uZoneCount) break;
    vec4 za = uZoneA[i];
    if (int(za.x) != ring) continue;
    if (ring != 0 && int(za.y) != idx) continue;
    vec4 zb = uZoneB[i];
    float amp = za.w;
    vec3 fam = FAMG[clamp(int(zb.w), 0, 4)];
    float rel = mix(1.0, 0.45, zb.z);
    float flash = exp(-zb.x * 3.0) * 0.2;
    float prof = ring == 0 ? (1.0 - smoothstep(r0 * 0.5, r0, r)) : (ring == 4 ? exp(-(r - r3) / (0.3 * uR)) : sin(bandT * PI));
    float angSoft = ring == 0 ? 1.0 : (1.0 - smoothstep(0.32, 0.5, abs(tt - 0.5)));
    float gwt = amp * (0.22 + flash) * prof * angSoft * rel;
    zoneGlow += fam * gwt;
    zoneTint += fam * gwt;
    zoneW += gwt;
    float trails = 1.0 + 2.0 * zb.y;
    for (int k = 0; k < 3; k++) {
      if (float(k) >= trails) break;
      float fk = float(k);
      float ws = waveSample(fract(tt + fk * 0.035 * sin(zb.x * 0.7)), za.z);
      float rw = rMid + ws * amp * band * 0.38 + fk * 4.0;
      float dl = abs(r - rw) - 0.9;
      float cov = (1.0 - smoothstep(0.0, aa * 2.0, dl)) * (1.0 - fk * 0.32) * angSoft * rel;
      waveCol += fam * cov * (0.55 + 0.6 * amp);
      waveA += cov;
      zoneGlow += fam * glowOf(dl, 0.35) * 0.12 * amp * angSoft * rel * (1.0 - fk * 0.32);
    }
  }
  vec3 colZoneDark = col + zoneGlow;
  vec3 colZoneLight = mix(col, zoneTint / max(zoneW, 1e-4) * 0.8, min(zoneW, 1.0) * 0.7);
  col = mix(colZoneDark, colZoneLight, uTheme);
  waveA = min(waveA, 1.0);
  col = mix(col + waveCol, col * (1.0 - waveA) + waveCol, uTheme);

  // ---------------------------------------------------------------- gold line assembly
  goldCov = clamp(goldCov, 0.0, 1.0) * rv;
  darkCov = clamp(darkCov, 0.0, 1.0) * rv;
  col = mix(col, lineDk, darkCov * 0.8);
  col = mix(col, lineCol, goldCov);
  col += lineHi * goldCov * goldCov * 0.3 * dark;
#ifndef MRT
  // tiers 0–1: SDF fake glow instead of bloom
  col += lineCol * clamp(glow, 0.0, 1.5) * 0.22 * rv * dark;
#endif

  // ---------------------------------------------------------------- sunburst (chord moment), 72 rays
  float sb = pow(0.5 + 0.5 * cos(a * 72.0 + uTime * 0.04), 7.0) * uSunburst * 1.5 * smoothstep(r0, r0 * 3.0, r);
  col += lineHi * sb * mix(1.0, -0.6, uTheme);
  emis += lineHi * sb;

  // ---------------------------------------------------------------- water: caustic brightness + gold specular
  col *= 1.0 + hC * 0.55;
  vec3 nrm = normalize(vec3(-grad * 30.0, 1.0));
  vec3 H = normalize(vec3(0.6, 0.5, 0.18) + vec3(0.0, 0.0, 1.0));
  float spec = pow(max(dot(nrm, H), 0.0), 48.0);
  col += lineHi * spec * 0.9;
  emis += lineHi * spec * 0.5;

  // calm mode: analytic single-ring ripple, brightness only, no displacement
  for (int i = 0; i < 8; i++) {
    if (i >= uCalmRingCount) break;
    vec4 cr = uCalmRings[i];
    float age = uWall - cr.z;
    if (age < 0.0 || age > 2.5) continue;
    float rad = age * 220.0;
    float dd = abs(length(p - cr.xy) - rad);
    col += lineHi * exp(-dd * dd / 196.0) * exp(-age * 1.6) * cr.w * 0.25 * mix(1.0, -0.5, uTheme);
  }

  // ---------------------------------------------------------------- label backing discs (undisplaced coords)
  float lb = 0.0;
  for (int i = 0; i < 48; i++) {
    if (i >= uLabelCount) break;
    float dl = length(p - uLabels[i] * uCanvasCss);
    lb = max(lb, 1.0 - smoothstep(uLabelRadius * 0.5, uLabelRadius, dl));
  }
  col = mix(col, mix(vec3(0.03, 0.03, 0.045), vec3(0.16, 0.12, 0.14), uTheme), lb * mix(0.6, 0.45, uTheme));

  // grain + dither
  float gr = hash21(p + fract(uTime) * 100.0) - 0.5;
  col += gr * (1.0 / 255.0 + 0.004 + 0.008 * dark);

  outColor = vec4(clamp(col, 0.0, 1.0), 1.0);
#ifdef MRT
  vec3 e = emis + lineCol * goldCov * 0.28 + zoneGlow * 1.1 + waveCol * 0.9;
  outEmissive = vec4(clamp(e, 0.0, 1.0), 1.0);
#endif
}
`;
